import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import "./calendarPage.css";
import { useLazyQuery } from "@apollo/client";
import {
  GET_SCHEDULE_MONTH_SUMMARY,
  GET_SCHEDULE_RANGE,
} from "../../../../utils/GraphQL/queries";
import { useSSEListener } from "../../../../utils/SSE/SSEContext";
import IconButton from "@mui/material/IconButton";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import CloseIcon from "@mui/icons-material/Close";
import HearingIcon from "@mui/icons-material/Hearing";
import AdminBDRSpinner from "../../adminComponents/AdminBDRSpinner/AdminBDRSpinner";
import CreateEventModal from "../../adminComponents/CreateEventModal/CreateEventModal";
import WeekModal from "../../adminComponents/WeekModal/WeekModal";
import DayViewModal from "../../adminComponents/DayViewModal/DayViewModal";
import AIAssignmentPanel from "../../adminComponents/AIAssignmentPanel/AIAssignmentPanel";
import EventFilterToggle from "../../adminComponents/EventFilterToggle/EventFilterToggle";
import Auth from "../../../../utils/auth";
import { hasRole } from "../../../../utils/permissions";
import {
  EVENT_FILTER_ALL,
  EVENT_FILTER_PERSONAL,
  readStoredEventFilter,
  writeStoredEventFilter,
  mergeSummaryForFilter,
  summaryCountLabel,
} from "../../../../utils/calendarPersonalEvents";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i).toLocaleDateString("en-US", { month: "long" })
);

/*
 * `focusEvent` — { dayMs, eventId } — is the Tasks console's Avail. Sooner
 * list asking for a specific event to be shown (2026-10-07).
 *
 * It arrives as a PROP rather than through the router because this page is a
 * MODAL owned by AdminNavbar, not a route: there is nothing to navigate to,
 * and `open` lives in the navbar. The navbar turns a window CustomEvent into
 * this prop — see `bdr:open-calendar-event` there.
 */
const CalendarPage = ({ open, onClose, focusEvent = null }) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Active view: 'week' (default), 'month', or 'day'
  const [activeView, setActiveView] = useState("week");

  /* A focused event always means the WEEK view on that event's day — the view
     the owner asked for, and the one that hosts the detail popup.

     `currentMonth` is NORMALISED to the 1st at 00:00, because that is a
     file-wide invariant: every other writer builds it as `new Date(y, m, 1)`,
     the render-phase reset COMPARES against that exact value, and
     `fetchMonthData` uses `currentMonth.getTime()` directly as the month
     query's start bound. Storing the event's own mid-month instant here would
     make a later switch to Month view fetch from that instant instead of the
     1st, silently reporting zero for every earlier day of the month while the
     grid still drew the right month. The DAY itself is not lost — it travels
     to WeekModal as `initialDayMs`. */
  useEffect(() => {
    if (!open || !focusEvent?.eventId) return;
    setActiveView("week");
    if (focusEvent.dayMs) {
      const d = new Date(Number(focusEvent.dayMs));
      setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [open, focusEvent?.eventId, focusEvent?.dayMs]);

  // When a day cell in the month grid is clicked, route into DayViewModal
  // pre-targeted at that day. Cleared whenever the user leaves day view
  // (switchToWeek/Month) and on each parent-open so subsequent calendar
  // opens always default to today rather than re-snapping to a stale click.
  const [dayViewInitialDay, setDayViewInitialDay] = useState(null);

  // Render-phase reset: snap currentMonth/activeView/dayViewInitialDay
  // back to defaults each time the calendar transitions closed→open.
  // Done during render (not in a useEffect) so children's mount-time
  // effects observe the fresh values on their first render — otherwise
  // child effects fire before parent state updates commit, and WeekModal
  // would land on the previous session's stale month.
  const prevOpenRef = useRef(false);
  if (open !== prevOpenRef.current) {
    prevOpenRef.current = open;
    if (open) {
      const now = new Date();
      const todayMonthFirst = new Date(now.getFullYear(), now.getMonth(), 1);
      if (currentMonth.getTime() !== todayMonthFirst.getTime()) {
        setCurrentMonth(todayMonthFirst);
      }
      if (activeView !== "week") setActiveView("week");
      if (dayViewInitialDay !== null) setDayViewInitialDay(null);
    }
  }

  // Create event modal state. `createEventType` seeds the modal's type
  // selector: "job" from "Create Event +", "personal" from the SIX button.
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [createEventType, setCreateEventType] = useState("job");

  // Personal ("SIX") events (2026-09-11): admin-only chrome + opt-in fetch.
  // The JWT permission string is decoded once per mount — it cannot change
  // without a re-login, which remounts the whole admin shell.
  const isAdmin = useMemo(() => hasRole(Auth.getPermissions(), "admin"), []);

  // All | Personal | Work filter, shared by every calendar surface and
  // persisted per browser (`bdr-calendar-event-filter`). Render-time only —
  // changing it never refetches. Non-admins never see personal items, so
  // their effective filter is pinned to "all" even if a shared browser has a
  // stale "personal" value stored.
  const [storedEventFilter, setStoredEventFilter] = useState(readStoredEventFilter);
  const eventFilter = isAdmin ? storedEventFilter : EVENT_FILTER_ALL;
  const setEventFilter = useCallback((next) => {
    setStoredEventFilter(next);
    writeStoredEventFilter(next);
  }, []);

  // AI Assignment panel state
  const [showAIPanel, setShowAIPanel] = useState(false);

  // Picker state (for month view)
  const [pickerOpen, setPickerOpen] = useState(null); // null | 'month' | 'year'
  const pickerRef = useRef(null);
  const yearPickerRef = useRef(null);

  // SSE stale tracking: true when a calendar_update arrives while the modal is closed
  const staleRef = useRef(false);

  const [getMonthSummary] = useLazyQuery(GET_SCHEDULE_MONTH_SUMMARY);
  const [getScheduleRange] = useLazyQuery(GET_SCHEDULE_RANGE);

  const fetchMonthData = useCallback(async () => {
    setLoading(true);
    const monthStart = currentMonth.getTime().toString();
    const lastDay = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0,
      23,
      59,
      59
    );
    const monthEnd = lastDay.getTime().toString();

    try {
      const { data } = await getMonthSummary({
        // includePersonal is the opt-in for personal ("SIX") rows; the server
        // honours it only for admins, so sending it for non-admins is inert.
        variables: {
          monthStart,
          monthEnd,
          onlyGetInvoiced: false,
          includePersonal: isAdmin,
        },
        fetchPolicy: "network-only",
      });
      setSummaries(data?.getScheduleMonthSummary || []);
    } catch (err) {
      console.error("Error fetching month summary:", err);
    }
    setLoading(false);
  }, [currentMonth, getMonthSummary, isAdmin]);

  // SSE handler: refetch month view when visible, mark stale when closed.
  // WeekModal and DayViewModal each have their own SSE listeners for live-updating.
  const handleCalendarSSEUpdate = useCallback(() => {
    if (open) {
      if (activeView === "month") {
        fetchMonthData();
      }
    } else {
      staleRef.current = true;
    }
  }, [open, activeView, fetchMonthData]);

  useSSEListener("calendar_update", handleCalendarSSEUpdate);

  // When the calendar reopens, refetch if data became stale while it was closed
  useEffect(() => {
    if (open && staleRef.current) {
      staleRef.current = false;
      if (activeView === "month") {
        fetchMonthData();
      }
    }
  }, [open, activeView, fetchMonthData]);

  // Fetch month data when month view becomes active or month changes
  useEffect(() => {
    if (open && activeView === "month") {
      fetchMonthData();
    }
  }, [open, activeView, fetchMonthData]);

  // Click-outside to close pickers
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  // Auto-scroll year picker to active year
  useEffect(() => {
    if (pickerOpen === "year" && yearPickerRef.current) {
      const active = yearPickerRef.current.querySelector(".active");
      if (active) active.scrollIntoView({ block: "center" });
    }
  }, [pickerOpen]);

  const handlePrevMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
  };

  // Month-grid day click → open DayViewModal targeted at that specific day.
  // Replaces the standalone DayModal entry point so all day-detail views
  // share one modal (mini-month nav + priority notes panel + timeline).
  const handleDayClick = (dayStartMs) => {
    if (!dayStartMs) return;
    switchToDay(new Date(parseInt(dayStartMs)));
  };

  const handleEventCreated = () => {
    setCreateEventOpen(false);
    if (activeView === "month") fetchMonthData();
  };

  // Toggle between week and month views with animation delay
  const switchToMonth = () => {
    setDayViewInitialDay(null);
    setActiveView("month");
  };

  const switchToWeek = () => {
    setDayViewInitialDay(null);
    setActiveView("week");
  };

  // specificDay (Date|null): when present, DayViewModal lands on that date
  // and currentMonth is synced so a subsequent View Week jumps to the same
  // month. When null (e.g. WeekModal's "View Day" button), DayViewModal
  // falls back to its today-or-currentMonth default.
  const switchToDay = (specificDay = null) => {
    if (specificDay) {
      setDayViewInitialDay(specificDay);
      setCurrentMonth(
        new Date(specificDay.getFullYear(), specificDay.getMonth(), 1)
      );
    } else {
      setDayViewInitialDay(null);
    }
    setActiveView("day");
  };

  const handleClose = () => {
    onClose();
  };

  const togglePicker = (type) => {
    setPickerOpen((prev) => (prev === type ? null : type));
  };

  // Build calendar grid
  const buildCalendarCells = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDayOfMonth.getDay();

    const cells = [];
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;

    // The server keys each day bucket by the CENTRAL NOON of that day (phase
    // PX, 2026-09-20) — never by local midnight. The old integer-day keys
    // drifted an hour as soon as a DST transition fell inside the window, and
    // because this map was matched by STRICT EQUALITY the grid did not move
    // its dots, it lost them: every cell from 2026-03-09 to 03-31 rendered
    // empty. Key by the LOCAL calendar day the anchor falls in instead — a
    // noon anchor resolves to the same calendar day in every US zone, so this
    // is stable no matter where the browser is.
    const summaryMap = {};
    summaries.forEach((s) => {
      const ms = parseInt(s.dateMs, 10);
      if (Number.isNaN(ms)) return;
      const d = new Date(ms);
      summaryMap[`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`] = s;
    });

    // Leading days from previous month
    const prevMonthLast = new Date(year, month, 0);
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLast.getDate() - i;
      const dt = new Date(year, month - 1, day);
      cells.push({ date: dt, otherMonth: true });
    }

    // Days of current month
    for (let d = 1; d <= lastDayOfMonth.getDate(); d++) {
      const dt = new Date(year, month, d);
      const dtStr = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
      const isToday = dtStr === todayStr;

      const dayStart = new Date(year, month, d);
      const dayStartMs = dayStart.getTime().toString();
      // `dtStr` is already this cell's local Y-M-D — the same shape the
      // summary map is now keyed by. `dayStartMs` still rides on the cell for
      // the day-range query; it is no longer the summary lookup key.
      const summary = summaryMap[dtStr];

      cells.push({
        date: dt,
        otherMonth: false,
        isToday,
        summary,
        dayStartMs,
        dayEndMs: new Date(year, month, d, 23, 59, 59).getTime().toString(),
      });
    }

    // Trailing days
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const dt = new Date(year, month + 1, i);
        cells.push({ date: dt, otherMonth: true });
      }
    }

    return cells;
  };

  const cells = buildCalendarCells();

  return (
    <>
      {/* Month View Dialog */}
      <BdrModal
        open={open && activeView === "month"}
        onClose={handleClose}
        /* bare: .calendar-page below carries its own panel, so the shell
           must not draw a second frame around it */
        variant="bare"
        size="xl"
        showHeader={false}
        ariaLabel="Calendar month view"
        /* Tagged so a walkthrough won't START over an open calendar in any
           view (tours/tourModals.js) — no step shows inside it. onClose is
           handleClose -> the navbar's close, state only. */
        tourTag="calendar-month"
      >
        <div className="calendar-page bdr-grad-container-med bdr-border-std bdr-shadow-depth">
          {/* Close button */}
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#fff",
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Month navigation header */}
          <div className="calendar-header">
            <button className="calendar-nav-btn" onClick={handlePrevMonth}>
              &#8249;
            </button>
            <h2 ref={pickerRef} className="calendar-month-year-label">
              <span
                className="calendar-picker-trigger"
                onClick={() => togglePicker("month")}
              >
                {currentMonth.toLocaleDateString("en-US", { month: "long" })}
              </span>{" "}
              <span
                className="calendar-picker-trigger"
                onClick={() => togglePicker("year")}
              >
                {currentMonth.getFullYear()}
              </span>

              {pickerOpen === "month" && (
                <div className="calendar-picker-dropdown calendar-month-picker">
                  {MONTH_NAMES.map((name, i) => (
                    <div
                      key={i}
                      className={`calendar-picker-option${
                        i === currentMonth.getMonth() ? " active" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentMonth(
                          new Date(currentMonth.getFullYear(), i, 1)
                        );
                        setPickerOpen(null);
                      }}
                    >
                      {name}
                    </div>
                  ))}
                </div>
              )}

              {pickerOpen === "year" && (
                <div
                  className="calendar-picker-dropdown calendar-year-picker"
                  ref={yearPickerRef}
                >
                  {Array.from({ length: 21 }, (_, i) => {
                    const year = currentMonth.getFullYear() - 10 + i;
                    return (
                      <div
                        key={year}
                        className={`calendar-picker-option${
                          year === currentMonth.getFullYear() ? " active" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentMonth(
                            new Date(year, currentMonth.getMonth(), 1)
                          );
                          setPickerOpen(null);
                        }}
                      >
                        {year}
                      </div>
                    );
                  })}
                </div>
              )}
            </h2>
            <button className="calendar-nav-btn" onClick={handleNextMonth}>
              &#8250;
            </button>
          </div>

          {/* Control buttons row */}
          <div className="calendar-controls">
            {isAdmin && (
              <EventFilterToggle value={eventFilter} onChange={setEventFilter} />
            )}
            <button
              className={`calendar-week-btn${showAIPanel ? " active-ai" : ""}`}
              onClick={() => setShowAIPanel(!showAIPanel)}
            >
              {showAIPanel ? "Close AI" : "AI Assign"}
            </button>
            <button
              className="calendar-week-btn"
              onClick={switchToWeek}
            >
              View Week
            </button>
            <button
              className="calendar-week-btn"
              /* Wrapped, not passed bare: onClick hands the handler a click
                 EVENT, which lands in `specificDay` and defeats its `= null`
                 default — then specificDay.getFullYear() throws. The other
                 caller below already wraps it for this reason. */
              onClick={() => switchToDay()}
            >
              View Day
            </button>
            <button
              className="calendar-create-btn bdr-grad-cal-green"
              onClick={() => {
                setCreateEventType("job");
                setCreateEventOpen(true);
              }}
            >
              Create Event +
            </button>
            {isAdmin && (
              <button
                className="calendar-create-btn bdr-grad-cal-green calendar-six-btn"
                title="Personal event"
                onClick={() => {
                  setCreateEventType("personal");
                  setCreateEventOpen(true);
                }}
              >
                SIX
                <HearingIcon sx={{ fontSize: 16 }} />
              </button>
            )}
          </div>

          {showAIPanel && (
            <AIAssignmentPanel
              showDatePickers={true}
              onClose={() => setShowAIPanel(false)}
            />
          )}

          {loading ? (
            <div className="calendar-loading">
              <AdminBDRSpinner message="Loading..." />
            </div>
          ) : (
            <div className="calendar-grid">
              {DAY_NAMES.map((name) => (
                <div key={name} className="calendar-day-header">
                  {name}
                </div>
              ))}

              {cells.map((cell, idx) => {
                const classes = ["calendar-day-cell"];
                if (cell.otherMonth) classes.push("other-month");
                if (cell.isToday) classes.push("today");
                // Dots + badge re-derived per filter: a personal-only day
                // reports count "0" with its colours in personalUserColors.
                const merged = cell.summary
                  ? mergeSummaryForFilter(cell.summary, eventFilter)
                  : null;

                return (
                  <div
                    key={idx}
                    className={classes.join(" ")}
                    onClick={() => {
                      if (!cell.otherMonth && cell.dayStartMs) {
                        handleDayClick(cell.dayStartMs);
                      }
                    }}
                  >
                    <span className="calendar-day-number">
                      {cell.date.getDate()}
                    </span>

                    {merged && (
                      <>
                        <div className="calendar-day-dots">
                          {merged.dots.slice(0, 5).map((color, i) => (
                            <span
                              key={i}
                              className="calendar-day-dot"
                              style={{
                                backgroundColor: color || "#9e9e9e",
                              }}
                            />
                          ))}
                        </div>
                        {merged.count > 0 && (
                          <span className="calendar-day-count">
                            {summaryCountLabel(merged)}
                          </span>
                        )}
                        {/* Priority notes belong to jobs — never shown under
                            the "Personal" filter (2026-09-14). */}
                        {cell.summary.hasPriorityNotes &&
                          eventFilter !== EVENT_FILTER_PERSONAL && (
                            <span className="calendar-priority-icon">!</span>
                          )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </BdrModal>

      {/* Week View */}
      <WeekModal
        open={open && activeView === "week"}
        onClose={handleClose}
        currentMonth={currentMonth}
        setCurrentMonth={setCurrentMonth}
        getScheduleRange={getScheduleRange}
        onSwitchToMonth={switchToMonth}
        onSwitchToDay={() => switchToDay()}
        isAdmin={isAdmin}
        eventFilter={eventFilter}
        onEventFilterChange={setEventFilter}
        initialDayMs={focusEvent?.dayMs || null}
        focusEventId={focusEvent?.eventId || null}
      />

      {/* Day View (single-day column + mini-month nav).
          Doubles as the per-day detail surface that month-grid clicks open
          via initialDay — replaces the standalone DayModal. */}
      <DayViewModal
        open={open && activeView === "day"}
        onClose={handleClose}
        currentMonth={currentMonth}
        setCurrentMonth={setCurrentMonth}
        initialDay={dayViewInitialDay}
        getScheduleRange={getScheduleRange}
        getMonthSummary={getMonthSummary}
        onSwitchToWeek={switchToWeek}
        onSwitchToMonth={switchToMonth}
        isAdmin={isAdmin}
        eventFilter={eventFilter}
        onEventFilterChange={setEventFilter}
      />

      <CreateEventModal
        open={createEventOpen}
        onClose={() => setCreateEventOpen(false)}
        onEventCreated={handleEventCreated}
        initialEventType={createEventType}
      />

    </>
  );
};

export default CalendarPage;
